import { useCallback, useRef, useState } from "react";
import { API } from "../../services/api";

function isCnoRowId(id) {
    return typeof id === "string" && id.startsWith("cno:");
}

function cnoServerId(id) {
    return Number(String(id).replace("cno:", ""));
}

function normalizeCnoRow(row) {
    if (!row) return row;
    const deliveryId = row.deliveryId ?? row.commandePatientId;
    return {
        ...row,
        id: `cno:${row.id}`,
        sourceType: "CNO",
        deliveryId,
        destination: "ETIQUETAGE",
        statut: row.statut === "PREVU" ? null : row.statut,
        classification: "CNO",
        classificationMedicament: "CNO",
        dateReception: row.deliveryDate ? `${row.deliveryDate}T00:00:00` : null,
        commandeId: `cno-delivery:${deliveryId}`,
        commandePatientId: `cno-patient:${row.patientId}`,
        ehpadNom: "CNO",
        ehpadCode: "CNO",
    };
}

function sortProductionRows(rows) {
    return [...rows].sort((a, b) => {
        if (!!a.productionValidated === !!b.productionValidated) return 0;
        return a.productionValidated ? 1 : -1;
    });
}

export function useOptimisticProductionRows() {
    const [rows, setRows] = useState([]);
    const pendingRef = useRef({});
    const dirtyRef = useRef({});
    const seqRef = useRef(0);

    const mergeWithPending = useCallback((serverRow, currentRow) => {
        if (!currentRow) return serverRow;
        const pendingFields = pendingRef.current[serverRow.id] || {};
        const hasPending = Object.keys(pendingFields).length > 0;
        const dirtyFields = dirtyRef.current[serverRow.id]?.optimisticFields;
        if (!hasPending && !dirtyFields) {
            return currentRow?._saved ? { ...serverRow, _saved: true } : serverRow;
        }

        const pendingValues = Object.keys(pendingFields).reduce((acc, field) => {
            acc[field] = currentRow[field];
            return acc;
        }, { ...(dirtyFields || {}) });

        return { ...serverRow, ...pendingValues, _saving: hasPending, _dirty: !!dirtyFields, _saved: !hasPending && !dirtyFields && !!currentRow?._saved };
    }, []);

    const markSaved = useCallback((row, serverRow) => {
        if (row._saving || row._dirty) return { ...row, _saved: false, _saveError: false };
        return {
            ...row,
            _saving: false,
            _dirty: false,
            _saveError: false,
            _saved: serverRow?.saveAcknowledged === true || row._saved === true,
        };
    }, []);

    const applyServerRows = useCallback((serverRows) => {
        setRows(prev => {
            const currentById = new Map(prev.map(row => [row.id, row]));
            return sortProductionRows(serverRows).map(row => mergeWithPending(row, currentById.get(row.id)));
        });
    }, [mergeWithPending]);

    const appendServerRows = useCallback((serverRows) => {
        setRows(prev => {
            const currentById = new Map(prev.map(row => [row.id, row]));
            const nextById = new Map(prev.map(row => [row.id, row]));
            (serverRows || []).forEach(row => {
                nextById.set(row.id, mergeWithPending(row, currentById.get(row.id)));
            });
            return sortProductionRows([...nextById.values()]);
        });
    }, [mergeWithPending]);

    const patchRow = useCallback(async (id, requestFields, optimisticFields = requestFields) => {
        const seq = ++seqRef.current;
        const protectedFields = Object.keys(optimisticFields);

        pendingRef.current[id] = {
            ...(pendingRef.current[id] || {}),
            ...protectedFields.reduce((acc, field) => {
                acc[field] = seq;
                return acc;
            }, {}),
        };

        setRows(prev => prev.map(row => (
            row.id === id ? { ...row, ...optimisticFields, _saving: true, _saved: false, _saveError: false } : row
        )));

        try {
            const { data } = isCnoRowId(id)
                ? await API.patch(`/cno/patients/delivery-lines/${cnoServerId(id)}`, requestFields, { noRedirectOn401: true })
                : await API.patch(`/medicaments/${id}`, requestFields, { noRedirectOn401: true });
            const normalizedData = isCnoRowId(id) ? normalizeCnoRow(data) : data;
            setRows(prev => prev.map(row => {
                if (row.id !== id) return row;

                const pendingFields = pendingRef.current[id] || {};
                protectedFields.forEach(field => {
                    if (pendingFields[field] === seq) delete pendingFields[field];
                });
                if (Object.keys(pendingFields).length === 0) {
                    delete pendingRef.current[id];
                }

                return markSaved(mergeWithPending(normalizedData, row), normalizedData);
            }));
        } catch (e) {
            console.error(e);
            const pendingFields = pendingRef.current[id] || {};
            protectedFields.forEach(field => {
                if (pendingFields[field] === seq) delete pendingFields[field];
            });
            if (Object.keys(pendingFields).length === 0) {
                delete pendingRef.current[id];
            }
            setRows(prev => prev.map(row => (
                row.id === id ? { ...row, _saving: false, _saveError: true } : row
            )));
        }
    }, [markSaved, mergeWithPending]);

    const stageRow = useCallback((id, requestFields, optimisticFields = requestFields) => {
        dirtyRef.current[id] = {
            requestFields: {
                ...(dirtyRef.current[id]?.requestFields || {}),
                ...requestFields,
            },
            optimisticFields: {
                ...(dirtyRef.current[id]?.optimisticFields || {}),
                ...optimisticFields,
            },
        };

        setRows(prev => prev.map(row => (
            row.id === id ? { ...row, ...optimisticFields, _dirty: true, _saved: false, _saveError: false } : row
        )));
    }, []);

    const flushRows = useCallback(async (ids) => {
        const uniqueIds = [...new Set((ids || []).filter(id => dirtyRef.current[id]))];
        if (uniqueIds.length === 0) return true;

        const seq = ++seqRef.current;
        const protectedById = {};
        const flushedById = {};
        uniqueIds.forEach(id => {
            const dirty = dirtyRef.current[id];
            const optimisticFields = dirty?.optimisticFields || {};
            flushedById[id] = dirty;
            protectedById[id] = Object.keys(optimisticFields);
            pendingRef.current[id] = {
                ...(pendingRef.current[id] || {}),
                ...protectedById[id].reduce((acc, field) => {
                    acc[field] = seq;
                    return acc;
                }, {}),
            };
        });

        setRows(prev => prev.map(row => (
            uniqueIds.includes(row.id) ? { ...row, _saving: true, _dirty: false, _saved: false, _saveError: false } : row
        )));

        const cnoIds = uniqueIds.filter(isCnoRowId);
        const medicineIds = uniqueIds.filter(id => !isCnoRowId(id));
        const patches = medicineIds.map(id => ({
            id,
            patch: flushedById[id].requestFields,
        }));

        try {
            const medicinePromise = patches.length > 0
                ? API.patch("/medicaments/batch", patches, { noRedirectOn401: true })
                : Promise.resolve({ data: [] });
            const cnoPromises = cnoIds.map(id => API.patch(`/cno/patients/delivery-lines/${cnoServerId(id)}`, flushedById[id].requestFields, { noRedirectOn401: true }));
            const [medicineResponse, ...cnoResponses] = await Promise.all([medicinePromise, ...cnoPromises]);
            const data = [
                ...(medicineResponse.data || []),
                ...cnoResponses.map(response => normalizeCnoRow(response.data)),
            ];
            uniqueIds.forEach(id => {
                // Une nouvelle modification peut avoir été saisie pendant la requête.
                // Dans ce cas elle reste dirty et sera sauvegardée au prochain flush.
                if (dirtyRef.current[id] === flushedById[id]) {
                    delete dirtyRef.current[id];
                }
            });
            const serverById = new Map((data || []).map(row => [row.id, row]));
            setRows(prev => prev.map(row => {
                if (!uniqueIds.includes(row.id)) return row;

                const pendingFields = pendingRef.current[row.id] || {};
                protectedById[row.id].forEach(field => {
                    if (pendingFields[field] === seq) delete pendingFields[field];
                });
                if (Object.keys(pendingFields).length === 0) {
                    delete pendingRef.current[row.id];
                }

                const serverRow = serverById.get(row.id) || row;
                // L'API a accepté le batch : les valeurs effectivement envoyées sont
                // acquittées. On les conserve même si le DTO retourné est retardé ou
                // incomplet, puis mergeWithPending réapplique une éventuelle saisie
                // plus récente effectuée pendant la sauvegarde.
                const optimisticFields = flushedById[row.id]?.optimisticFields || {};
                const acknowledgedRow = {
                    ...serverRow,
                    ...(flushedById[row.id]?.requestFields || {}),
                };
                Object.entries(optimisticFields).forEach(([field, value]) => {
                    if (acknowledgedRow[field] == null || (acknowledgedRow[field] === false && value === true)) {
                        acknowledgedRow[field] = value;
                    }
                });
                return markSaved(mergeWithPending(acknowledgedRow, row), serverRow);
            }));
            return true;
        } catch (e) {
            console.error(e);
            uniqueIds.forEach(id => {
                const pendingFields = pendingRef.current[id] || {};
                protectedById[id].forEach(field => {
                    if (pendingFields[field] === seq) delete pendingFields[field];
                });
                if (Object.keys(pendingFields).length === 0) {
                    delete pendingRef.current[id];
                }
            });
            setRows(prev => prev.map(row => (
                uniqueIds.includes(row.id) ? { ...row, _saving: false, _dirty: true, _saveError: true } : row
            )));
            return false;
        }
    }, [markSaved, mergeWithPending]);

    return { rows, setRows, applyServerRows, appendServerRows, patchRow, stageRow, flushRows };
}
